class BupVideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundHandleViewerScroll = this.handleViewerScroll.bind(this);
    this.boundHandlePreviewScroll = this.handlePreviewScroll.bind(this);
    this.currentIndex = 0;
    this.scrollLockY = 0;
    this.scrollTicking = false;
    this.previewScrollTicking = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.collectElements();
    if (!this.viewer || !this.previewOpeners.length || !this.slides.length) return;
    this.bindEvents();
    this.createObservers();
    this.syncAllPreviewMuteButtons();
    this.syncAllViewerMuteButtons();
    this.refreshPreviewPlayback();
  }

  disconnectedCallback() {
    this.previewObserver?.disconnect();
    this.slideObserver?.disconnect();
    document.removeEventListener("keydown", this.boundHandleKeydown);
    window.removeEventListener("resize", this.boundHandleResize);
    this.viewerTrack?.removeEventListener("scroll", this.boundHandleViewerScroll);
    this.previewTrack?.removeEventListener("scroll", this.boundHandlePreviewScroll);
    this.unlockScroll();
  }

  collectElements() {
    this.previewTrack = this.querySelector("[data-preview-track]");
    this.previewOpeners = [...this.querySelectorAll("[data-open-viewer]")];
    this.previewCards = [...this.querySelectorAll(".bup-vc__card")];
    this.previewVideos = [...this.querySelectorAll("[data-preview-video]")];
    this.previewMuteButtons = [...this.querySelectorAll("[data-preview-mute-toggle]")];
    this.previewNavButtons = [...this.querySelectorAll("[data-preview-nav-dot]")];
    this.viewer = this.querySelector("[data-viewer]");
    this.viewerTrack = this.querySelector("[data-viewer-track]");
    this.slides = [...this.querySelectorAll("[data-viewer-slide]")];
    this.viewerVideos = [...this.querySelectorAll("[data-viewer-video]")];
    this.muteButtons = [...this.querySelectorAll("[data-mute-toggle]")];
    this.closeButtons = [...this.querySelectorAll("[data-close-viewer]")];
    this.panels = [...this.querySelectorAll("[data-panel-index]")];
  }

  bindEvents() {
    this.previewOpeners.forEach((button) => {
      button.addEventListener("click", () => {
        this.openViewer(Number(button.dataset.openViewer || 0));
      });
    });

    this.previewMuteButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.togglePreviewMute(Number(button.dataset.previewMuteToggle || 0));
      });
    });

    this.previewNavButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.scrollPreviewTo(Number(button.dataset.previewNavDot || 0));
      });
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", () => this.closeViewer());
    });

    this.muteButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const index = Number(button.dataset.muteToggle || 0);
        const video = this.viewerVideos[index];
        if (!video) return;
        video.muted = !video.muted;
        this.syncMuteButton(index);
      });
    });

    this.viewerTrack?.addEventListener("scroll", this.boundHandleViewerScroll, {
      passive: true,
    });
    this.previewTrack?.addEventListener("scroll", this.boundHandlePreviewScroll, {
      passive: true,
    });
    document.addEventListener("keydown", this.boundHandleKeydown);
    window.addEventListener("resize", this.boundHandleResize);
  }

  createObservers() {
    this.previewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.dataset.inViewport = entry.isIntersecting ? "true" : "false";
        });
        this.refreshPreviewPlayback();
      },
      {
        threshold: 0.6,
      }
    );

    this.previewVideos.forEach((video) => this.previewObserver.observe(video));

    if (!this.viewerTrack) return;

    this.slideObserver = new IntersectionObserver(
      (entries) => {
        const visibleSlide = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleSlide) return;
        this.setActiveSlide(Number(visibleSlide.target.dataset.viewerSlide || 0));
      },
      {
        root: this.viewerTrack,
        threshold: [0.45, 0.6, 0.8],
      }
    );

    this.slides.forEach((slide) => this.slideObserver.observe(slide));
  }

  handleKeydown(event) {
    if (!this.viewer || this.viewer.hidden) return;

    if (event.key === "Escape") {
      this.closeViewer();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.goToSlide(Math.min(this.currentIndex + 1, this.slides.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.goToSlide(Math.max(this.currentIndex - 1, 0));
    }
  }

  handleResize() {
    this.updatePreviewNavFromScroll();
    if (!this.viewer || this.viewer.hidden) return;
    this.goToSlide(this.currentIndex, "auto");
    this.updateActiveSlideFromScroll();
  }

  handleViewerScroll() {
    if (!this.viewer || this.viewer.hidden || this.scrollTicking) return;

    this.scrollTicking = true;
    window.requestAnimationFrame(() => {
      this.updateActiveSlideFromScroll();
      this.scrollTicking = false;
    });
  }

  handlePreviewScroll() {
    if (this.previewScrollTicking) return;

    this.previewScrollTicking = true;
    window.requestAnimationFrame(() => {
      this.updatePreviewNavFromScroll();
      this.previewScrollTicking = false;
    });
  }

  openViewer(index) {
    if (!this.viewer || !this.viewerTrack) return;

    this.pauseAllPreviewVideos();
    this.lockScroll();
    this.viewer.hidden = false;
    window.requestAnimationFrame(() => {
      this.viewer.classList.add("is-open");
      this.goToSlide(index, "auto");
      this.setActiveSlide(index, true);
      window.requestAnimationFrame(() => this.updateActiveSlideFromScroll());
    });
  }

  closeViewer() {
    if (!this.viewer || this.viewer.hidden) return;

    this.viewer.classList.remove("is-open");
    this.pauseAllViewerVideos();
    window.setTimeout(() => {
      this.viewer.hidden = true;
    }, 280);
    this.unlockScroll();
    this.refreshPreviewPlayback();
  }

  goToSlide(index, behavior = "smooth") {
    const slide = this.slides[index];
    if (!slide || !this.viewerTrack) return;

    this.viewerTrack.scrollTo({
      top: slide.offsetTop,
      behavior,
    });
  }

  updateActiveSlideFromScroll() {
    if (!this.viewerTrack || !this.slides.length) return;

    const trackCenter =
      this.viewerTrack.scrollTop + this.viewerTrack.clientHeight / 2;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const slideCenter = slide.offsetTop + slide.offsetHeight / 2;
      const distance = Math.abs(slideCenter - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.setActiveSlide(closestIndex);
  }

  setActiveSlide(index, force = false) {
    if (!force && this.currentIndex === index) return;
    this.currentIndex = index;

    this.slides.forEach((slide) => {
      slide.classList.toggle(
        "is-active",
        Number(slide.dataset.viewerSlide || 0) === index
      );
    });

    this.panels.forEach((panel) => {
      panel.classList.toggle(
        "is-active",
        Number(panel.dataset.panelIndex || 0) === index
      );
    });

    this.viewerVideos.forEach((video, videoIndex) => {
      if (videoIndex === index) {
        this.safePlay(video);
      } else {
        video.pause();
        video.currentTime = video.currentTime;
      }
      this.syncMuteButton(videoIndex);
    });
  }

  togglePreviewMute(index) {
    const targetVideo = this.previewVideos[index];
    if (!targetVideo) return;

    const shouldUnmute = targetVideo.muted;
    this.previewVideos.forEach((video, videoIndex) => {
      video.muted = videoIndex === index ? !video.muted : true;
    });

    if (shouldUnmute) {
      this.safePlay(targetVideo);
    }

    this.syncAllPreviewMuteButtons();
  }

  syncPreviewMuteButton(index) {
    const button = this.previewMuteButtons.find(
      (item) => Number(item.dataset.previewMuteToggle || 0) === index
    );
    const video = this.previewVideos[index];
    if (!button || !video) return;

    const isUnmuted = !video.muted;
    button.classList.toggle("is-unmuted", isUnmuted);
    button.setAttribute("aria-label", isUnmuted ? "Sesi Kapat" : "Sesi Aç");
  }

  syncAllPreviewMuteButtons() {
    this.previewVideos.forEach((_, index) => this.syncPreviewMuteButton(index));
  }

  scrollPreviewTo(index, behavior = "smooth") {
    const card = this.previewCards[index];
    if (!card || !this.previewTrack) return;

    this.previewTrack.scrollTo({
      left: card.offsetLeft,
      behavior,
    });
    this.setActivePreviewIndex(index);
  }

  updatePreviewNavFromScroll() {
    if (!this.previewTrack || !this.previewCards.length || !this.previewNavButtons.length) return;

    const trackCenter =
      this.previewTrack.scrollLeft + this.previewTrack.clientWidth / 2;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.previewCards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.setActivePreviewIndex(closestIndex);
  }

  setActivePreviewIndex(index) {
    this.previewNavButtons.forEach((button) => {
      const isActive = Number(button.dataset.previewNavDot || 0) === index;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  syncMuteButton(index) {
    const button = this.muteButtons.find(
      (item) => Number(item.dataset.muteToggle || 0) === index
    );
    const video = this.viewerVideos[index];
    if (!button || !video) return;

    button.classList.toggle("is-unmuted", !video.muted);
    button.textContent = video.muted ? "Sessiz" : "Ses Acik";
  }

  syncAllViewerMuteButtons() {
    this.viewerVideos.forEach((_, index) => this.syncMuteButton(index));
  }

  safePlay(video) {
    if (!video) return;
    const promise = video.play();
    if (promise && typeof promise.catch === "function") {
      promise.catch(() => {});
    }
  }

  pauseAllPreviewVideos() {
    this.previewVideos.forEach((video) => video.pause());
  }

  pauseAllViewerVideos() {
    this.viewerVideos.forEach((video) => video.pause());
  }

  refreshPreviewPlayback() {
    if (!this.viewer || !this.viewer.hidden) return;

    this.previewVideos.forEach((video) => {
      if (video.dataset.inViewport === "true") {
        this.safePlay(video);
      } else {
        video.pause();
      }
    });
    this.updatePreviewNavFromScroll();
  }

  lockScroll() {
    if (document.body.classList.contains("bup-vc-lock")) return;

    this.scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add("bup-vc-lock");
    document.body.classList.add("bup-vc-lock");
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.scrollLockY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

  unlockScroll() {
    if (!document.body.classList.contains("bup-vc-lock")) return;

    document.documentElement.classList.remove("bup-vc-lock");
    document.body.classList.remove("bup-vc-lock");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, this.scrollLockY);
  }
}

if (!customElements.get("bup-video-carousel")) {
  customElements.define("bup-video-carousel", BupVideoCarousel);
}
